
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface Notification {
  id: string;
  user_id: string;
  type: 'campaign' | 'finance' | 'system';
  category: string;
  title: string;
  message: string;
  metadata?: any;
  action_link?: string;
  triggered_on: string;
  expires_at?: string;
  read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
  organization_id?: string;
}

// Utility function for status colors
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'urgent':
    case 'high':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'low':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'info':
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
}

// Utility function to group notifications by date
export function groupNotificationsByDate(notifications: Notification[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const grouped = {
    today: [] as Notification[],
    yesterday: [] as Notification[],
    older: [] as Notification[]
  };

  notifications.forEach(notification => {
    const notificationDate = new Date(notification.triggered_on);
    const isToday = notificationDate.toDateString() === today.toDateString();
    const isYesterday = notificationDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      grouped.today.push(notification);
    } else if (isYesterday) {
      grouped.yesterday.push(notification);
    } else {
      grouped.older.push(notification);
    }
  });

  return grouped;
}

// Get all notifications for current user with organization scoping
export function useNotifications(options?: {
  filterType?: string;
  unreadOnly?: boolean;
}) {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['notifications', currentOrganization?.id, options?.filterType, options?.unreadOnly],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      if (!currentOrganization?.id) return [];

      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id);

      // Only filter by organization_id if it exists (for backwards compatibility)
      query = query.or(`organization_id.eq.${currentOrganization.id},organization_id.is.null`);

      // Apply category filter
      if (options?.filterType && options.filterType !== 'all') {
        query = query.eq('category', options.filterType);
      }

      // Apply unread filter
      if (options?.unreadOnly) {
        query = query.eq('read', false);
      }

      query = query.order('triggered_on', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!currentOrganization?.id,
  });
}

// Get unread notifications count with organization scoping
export function useUnreadNotificationsCount() {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['notifications-unread-count', currentOrganization?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      if (!currentOrganization?.id) return 0;

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .or(`organization_id.eq.${currentOrganization.id},organization_id.is.null`)
        .eq('read', false);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!currentOrganization?.id,
  });
}

// Get notifications by category with organization scoping
export function useNotificationsByCategory(category: string) {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['notifications', 'category', category, currentOrganization?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      if (!currentOrganization?.id) return [];

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .or(`organization_id.eq.${currentOrganization.id},organization_id.is.null`)
        .eq('category', category)
        .order('triggered_on', { ascending: false });

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!currentOrganization?.id,
  });
}

// Manual function to process notification queue (can be called from admin interface)
export function useProcessNotificationQueue() {
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('process_notification_queue');
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Notification queue processed successfully",
      });
    },
    onError: (error) => {
      console.error('Error processing notification queue:', error);
      toast({
        title: "Error",
        description: "Failed to process notification queue",
        variant: "destructive",
      });
    },
  });
}
